import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, ArrowRight } from 'lucide-react';
// Use react-router-dom to match your DestinationsPage setup
import { Link } from 'react-router-dom';

export const DestinationsSection = () => {
  const destinations = [
    {
      name: 'Victoria Falls',
      country: 'Zambia & Zimbabwe',
      description: 'Experience the thundering majesty of one of the world\'s largest waterfalls and enjoy Zambezi River adventures.',
      image: 'https://images.pexels.com/photos/1637146/pexels-photo-1637146.jpeg',
      highlights: ['Waterfall Tours', 'River Cruises', 'Bungee Jumping']
    },
    {
      name: 'South Luangwa',
      country: 'Zambia',
      description: 'Witness incredible wildlife concentrations in one of Africa\'s premier walking safari destinations.',
      image: 'https://images.pexels.com/photos/1670732/pexels-photo-1670732.jpeg',
      highlights: ['Walking Safaris', 'Leopard Spotting', 'Night Drives']
    },
    {
      name: 'Chobe National Park',
      country: 'Botswana',
      description: 'Home to the largest elephant population in Africa, offering spectacular river safaris.',
      image: 'https://images.pexels.com/photos/59989/elephant-herd-of-elephants-african-bush-elephant-africa-59989.jpeg',
      highlights: ['Elephant Herds', 'Boat Safaris', 'Big Five']
    },
    {
      name: 'Kafue National Park',
      country: 'Zambia',
      description: 'Explore vast wilderness areas with diverse ecosystems and exceptional wildlife viewing.',
      image: 'https://images.pexels.com/photos/35383072/pexels-photo-35383072.jpeg',
      highlights: ['Game Drives', 'Bird Watching', 'Wild Dogs']
    },
    {
      name: 'Maasai Mara',
      country: 'Kenya',
      description: 'Witness the great wildebeest migration and experience authentic Maasai culture.',
      image: 'https://images.pexels.com/photos/4577792/pexels-photo-4577792.jpeg',
      highlights: ['The Migration', 'Big Cats', 'Hot Air Balloons']
    },
    {
      name: 'Greater Mana Pools',
      country: 'Zimbabwe',
      description: 'A UNESCO World Heritage site offering pristine wilderness and walking safaris.',
      image: 'https://images.pexels.com/photos/695214/pexels-photo-695214.jpeg',
      highlights: ['Walking Safaris', 'Canoeing', 'Wild Dogs']
    }
  ];

  return (
    <section id="destinations" className="section-padding bg-background">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-4">
            Destinations
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-6 font-serif leading-tight">
            Explore Africa's Most Iconic Destinations
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            From thundering waterfalls to vast savannas teeming with wildlife, discover the extraordinary beauty and diversity of Southern and Eastern Africa.
          </p>
        </div>

        {/* Destinations Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {destinations.map((destination, index) => (
            <Card
              key={index}
              className="group overflow-hidden border-2 hover:border-primary/30 transition-all duration-300 flex flex-col"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={destination.image}
                  alt={destination.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent" />

                {/* Location Badge */}
                <div className="absolute top-4 left-4 bg-background/95 backdrop-blur-sm rounded-lg px-3 py-1.5 flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">{destination.country}</span>
                </div>

                {/* Destination Name on Image */}
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-2xl font-bold text-background font-serif">
                    {destination.name}
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-muted-foreground leading-relaxed mb-4 flex-1">
                  {destination.description}
                </p>

                {/* Highlights */}
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {destination.highlights.map((highlight, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-3 py-1 bg-accent/10 text-accent rounded-full font-medium"
                      >
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CTA Button linked to your Destinations Page */}
                <Button
                  asChild
                  variant="ghost"
                  className="w-full justify-between group/btn hover:bg-primary/5 mt-auto"
                >
                  <Link to="/destinations">
                    <span className="text-primary font-medium">Learn More</span>
                    <ArrowRight className="w-4 h-4 text-primary transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};